import styles from './singleOrderView.module.scss';
import {Card, Row, Col, Typography} from 'antd';
import moment from 'moment';
import {formatDecimal, formatDecimalWithCurrency} from './formatDecimal';

const {Text} = Typography;

const getCumulativeFillValue = input => {
  const direction = input?.direction;
  let amount = 'N/A';
  if (direction === 'BUY' && input?.fillValue) {
    amount = input.fillValue - input.feeCharged;
  } else if (direction === 'SELL' && input?.fillValue) {
    amount = input.fillValue;
  }
  return amount;
};

export const DerivedMetrics = ({data}) => {
  // Helpers
  const formatNumber = value => formatDecimal(value);

  const formatCurrency = value =>
    formatDecimalWithCurrency(value, data?.assetCurrency);

  const formatDate = dateLike =>
    dateLike ? moment(dateLike).format('DD MMM, YYYY. h:mmA') : 'N/A';

  const formatDuration = ms => {
    if (ms === null || ms === undefined || Number.isNaN(Number(ms))) {
      return 'N/A';
    }
    const duration = moment.duration(Math.max(0, Number(ms)));
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    const parts = [];
    if (days) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours) parts.push(`${hours} hr${hours !== 1 ? 's' : ''}`);
    if (minutes) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
    if (parts.length === 0)
      parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);
    return parts.join(' ');
  };

  // Source fields (available on the order)
  const originalQuantityRaw =
    data?.origination?.quantity ?? data?.quantity ?? null; // from origination.quantity
  const totalFilledQuantityRaw = data?.fillQuantity ?? null; // from fillQuantity
  const averageFillPriceRaw = data?.avgFillPrice ?? null; // from avgFillPrice
  const totalFeeRaw = data?.totalFee ?? null; // from totalFee (initial fee withheld)
  const lastFillAtRaw = data?.lastFillDate ?? null; // from lastFillDate
  const placedAtRaw = data?.placedAt ?? null; // from placedAt
  const settledAtRaw = data?.settlementDate ?? null; // from settlementDate
  const createdAtRaw = data?.createdAt ?? null; // from createdAt

  const orderHistory =
    data?.history?.length > 0
      ? data?.history?.find(item =>
          ['FILLED', 'PARTIALLY_FILLED', 'SETTLED'].includes(item?.status),
        )
      : null;

  const fillAtRaw =
    orderHistory?.input?.lastFillDate ?? orderHistory?.createdAt;

  const orderExecutionCount =
    data?.history?.length > 0
      ? data?.history?.filter(item =>
          ['FILLED', 'PARTIALLY_FILLED', 'SETTLED'].includes(item?.status),
        )?.length
      : 0;

  // Derived numbers
  const originalQuantity = parseFloat(originalQuantityRaw) || 0;
  const totalFilledQuantity = parseFloat(totalFilledQuantityRaw) || 0;
  const remainingQuantity =
    originalQuantity && totalFilledQuantity !== null
      ? originalQuantity - totalFilledQuantity
      : null;

  const fillRatePct =
    originalQuantity > 0
      ? (parseFloat(totalFilledQuantity) / parseFloat(originalQuantity)) * 100
      : null;

  const totalExecutedValue = getCumulativeFillValue(data);

  const feePerUnit =
    totalFeeRaw !== null && parseFloat(totalFilledQuantity) > 0
      ? parseFloat(totalFeeRaw) / parseFloat(totalFilledQuantity)
      : null;

  const timeToSettlementMs =
    settledAtRaw && createdAtRaw
      ? new Date(settledAtRaw).getTime() - new Date(createdAtRaw).getTime()
      : null;
  const timeToFillMs =
    lastFillAtRaw && createdAtRaw
      ? new Date(lastFillAtRaw).getTime() - new Date(createdAtRaw).getTime()
      : null;

  return (
    <Card title="Derived Metrics" className={styles.card}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Original Quantity:</Text>
            <Text>{formatNumber(originalQuantity, 0)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Total Filled Quantity:</Text>
            <Text>{formatNumber(totalFilledQuantity, 0)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Remaining Quantity:</Text>
            <Text>
              {remainingQuantity === null
                ? 'N/A'
                : formatNumber(remainingQuantity, 0)}
            </Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Fill Rate:</Text>
            <Text>
              {fillRatePct === null
                ? 'N/A'
                : `${formatNumber(fillRatePct, 2)}%`}
            </Text>
          </div>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Average Fill Price:</Text>
            <Text>{formatCurrency(averageFillPriceRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Total Executed Value:</Text>
            <Text>{formatCurrency(totalExecutedValue)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Total Fee (Initial Withheld):</Text>
            <Text>{formatCurrency(totalFeeRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Broker Executions Count:</Text>
            <Text>
              {data?.pastBrokerIds?.length > 0
                ? data?.pastBrokerIds?.length
                : 'N/A'}
            </Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Execution Events Count:</Text>
            <Text>{orderExecutionCount > 0 ? orderExecutionCount : 'N/A'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Fee Per Unit:</Text>
            <Text>{formatCurrency(feePerUnit)}</Text>
          </div>
        </Col>

        {/* Date metrics - only those available */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Created At:</Text>
            <Text>{formatDate(createdAtRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Placed At:</Text>
            <Text>{formatDate(placedAtRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>First Fill At:</Text>
            <Text>{formatDate(fillAtRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Last Fill At:</Text>
            <Text>{formatDate(lastFillAtRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Settled At:</Text>
            <Text>{formatDate(settledAtRaw)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Time To Settlement:</Text>
            <Text>{formatDuration(timeToSettlementMs)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <div className={styles.field}>
            <Text strong>Time To Fill:</Text>
            <Text>{formatDuration(timeToFillMs)}</Text>
          </div>
        </Col>
      </Row>
    </Card>
  );
};
